import type { SynthAttemptOutcome } from './audioContext';

type Reply = { v: 1; id: number; result: string; attempt?: number };
export interface FillAudioPort {
  onmessage: ((event: MessageEvent<Reply>) => void) | null;
  postMessage(message: unknown): void;
  start(): void;
}
export interface FillAudioRequest {
  /** JSON tuple: authenticated user, org, role, credential mode, venue, fill. */
  key: string;
  valid(): boolean;
  ready(): boolean;
  attempt(canStart: () => boolean): SynthAttemptOutcome;
}
export type CoordinationResult = 'fallback' | 'suppressed' | 'expired' | 'attempted' | 'unknown' | 'capacity';
const REQUEST_MS = 1000;

/** No timers/port registry in the worker per tab, and no broadcasts of account
 * identity. A timed-out claim is ambiguous: it must never fall back to playback.
 */
export class FillAudioCoordinator {
  private port: FillAudioPort | null = null;
  private opening: Promise<boolean> | null = null;
  private sequence = 0;
  private pending = new Map<number, (reply: Reply | null) => void>();
  constructor(private factory: () => FillAudioPort, private now = () => performance.now()) {}

  private rpc(message: Record<string, unknown>): Promise<Reply | null> {
    if (this.pending.size >= 1024) return Promise.resolve(null);
    const id = ++this.sequence;
    return new Promise(resolve => {
      const timer = setTimeout(() => finish(null), REQUEST_MS);
      const finish = (reply: Reply | null) => {
        clearTimeout(timer); this.pending.delete(id); resolve(reply);
      };
      this.pending.set(id, finish);
      try { this.port!.postMessage({ ...message, v: 1, id }); } catch { finish(null); }
    });
  }

  private open(): Promise<boolean> {
    if (this.opening) return this.opening;
    this.opening = (async () => {
      try {
        this.port = this.factory();
        this.port.onmessage = event => {
          const reply = event.data;
          if (reply?.v === 1) this.pending.get(reply.id)?.(reply);
        };
        this.port.start();
        return (await this.rpc({ type: 'hello' }))?.result === 'ready';
      } catch { return false; }
    })();
    return this.opening;
  }

  async request(request: FillAudioRequest): Promise<CoordinationResult> {
    if (!await this.open()) return 'fallback'; // no claim was sent
    while (request.valid() && request.ready()) {
      const sentAt = this.now();
      const reply = await this.rpc({ type: 'claim', key: request.key });
      // A resumed frozen tab may receive a queued reply before its timeout.
      if (!reply || this.now() - sentAt >= REQUEST_MS) return 'unknown';
      if (reply.result === 'suppress') return 'suppressed';
      // A dead owner's tab cannot report its own sound health. Let a surviving
      // peer surface the ambiguity without granting it permission to replay.
      if (reply.result === 'uncertain') return 'unknown';
      if (reply.result === 'capacity') return 'capacity';
      if (reply.result === 'wait') {
        await new Promise(resolve => setTimeout(resolve, 50));
        continue;
      }
      if (reply.result !== 'grant' || !Number.isSafeInteger(reply.attempt)) return 'unknown';
      let outcome: SynthAttemptOutcome | { kind: 'unknown' };
      try {
        outcome = request.attempt(() => request.valid() && request.ready() && this.now() - sentAt < REQUEST_MS);
      } catch { outcome = { kind: 'unknown' }; }
      // No reply is needed to permit another sound. Only a confirmed zero-start
      // report can release this attempt; all other outcomes remain consumed.
      void this.rpc({ type: 'outcome', key: request.key, attempt: reply.attempt, outcome: outcome.kind });
      return outcome.kind === 'unknown' ? 'unknown' : 'attempted';
    }
    return 'expired';
  }
}

let singleton: FillAudioCoordinator | null = null;
export function coordinateFillAudio(request: FillAudioRequest): Promise<CoordinationResult> | null {
  if (typeof SharedWorker === 'undefined') return null;
  singleton ??= new FillAudioCoordinator(() => new SharedWorker('/fill-audio-coordinator-v1.js',
    { name: 'g1-fill-audio-v1' }).port as FillAudioPort);
  return singleton.request(request);
}
